// The loop behind every globe on the page: lazy load, visibility, quality ladder,
// reduced motion and pause. The hero stage and the scroll stage differ only in WHERE
// the globe sits (`computeLayout`), WHEN it loads (`trigger`) and WHERE it looks
// (`getView`).
//
// CONTRACT (this is what keeps the hero inside Core Web Vitals)
//   - respects Save-Data and 2G: those visitors keep the CSS glow only
//   - paint first: a static CSS glow shows at once; the scene is a lazy chunk
//     (OGL + scene) fetched on idle or near the viewport, so none of it is in the
//     first JavaScript
//   - fixed size: an absolutely positioned box, so layout shift stays 0
//   - no layout reads in the frame loop: rects are read in a ResizeObserver
//     callback only, and the pointer is normalised by window size
//   - quality ladder: pixel ratio is capped at 2; if frames run slow it drops
//     to 1, and if they are still slow it stops on a still frame
//   - stop when unseen: paused off screen and in hidden tabs
//   - prefers-reduced-motion: one still frame, no loop, no control
//   - the visitor can pause every globe at once (WCAG 2.2.2): see globe-pause.ts
//
// Hero-only exception to the Ink "Don't" list (owner-approved): glow and additive
// blending. Decorative, aria-hidden.
'use client'

import { useEffect, useRef, useState, type RefObject } from 'react'
import type { GlobeLayout } from './probe-globe-scene'
import type { GlobeView } from './globe-views'
import { globePause } from './globe-pause'

type Globe = ReturnType<typeof import('./probe-globe-scene').createGlobe>

type Options = {
  hostRef: RefObject<HTMLDivElement | null>
  /** Reads layout. Called from a ResizeObserver only, never from the frame loop. */
  computeLayout: (hostRect: DOMRect) => GlobeLayout
  /** Where the globe looks right now. Omit for the free-drifting hero. */
  getView?: () => GlobeView | undefined
  /** Second of the story to start on, so the globe agrees with the card beside it. */
  storyStart: number
  /** `idle`: load when the browser is idle. `near`: load when the host nears the viewport. */
  trigger?: 'idle' | 'near'
  /** Grab-and-spin with the pointer (mouse, touch, pen) on the host. */
  interactive?: boolean
}

export function useGlobeLoop(options: Options) {
  const [live, setLive] = useState(false)
  const optsRef = useRef(options)
  // Keep the latest callbacks without re-running the setup effect below.
  useEffect(() => {
    optsRef.current = options
  })
  const stillRef = useRef<() => void>(() => {})

  useEffect(() => {
    const host = optsRef.current.hostRef.current
    if (!host) return

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    let disposed = false
    let globe: Globe | undefined
    let canvas: HTMLCanvasElement | undefined
    let raf = 0
    let visible = true
    // Full Retina sharpness (the restyled scene is light); the quality ladder
    // below still drops to 1x when frames are slow.
    let dpr = Math.min(window.devicePixelRatio || 1, 2)
    let prev = 0
    let t0 = 0
    let frames = 0
    let acc = 0
    const pointer = { x: 0, y: 0, tx: 0, ty: 0 }

    const layout = () => {
      if (!globe) return
      const hr = host.getBoundingClientRect()
      if (hr.width === 0 || hr.height === 0) return
      globe.resize(hr.width, hr.height, dpr, optsRef.current.computeLayout(hr))
    }

    const still = () => {
      if (!globe) return
      const view = optsRef.current.getView?.()
      if (view) globe.snapTo(view)
      globe.render(optsRef.current.storyStart, pointer, view)
    }
    stillRef.current = still

    const onPointer = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return
      pointer.tx = (e.clientX / (window.innerWidth || 1)) * 2 - 1
      pointer.ty = (e.clientY / (window.innerHeight || 1)) * 2 - 1
    }

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame)
      if (!globe || !visible || document.hidden || (globePause.get() && !held)) {
        prev = 0
        return
      }
      if (!t0) t0 = now
      if (!prev) prev = now
      const dt = now - prev
      prev = now

      // Quality ladder: judge the last 90 frames.
      acc += dt
      frames++
      if (frames === 90) {
        const avg = acc / frames
        frames = 0
        acc = 0
        if (avg > 28) {
          if (dpr > 1) {
            dpr = 1
            layout()
          } else {
            cancelAnimationFrame(raf)
            raf = 0
            setLive(false)
            return
          }
        }
      }
      const k = Math.min(1, (dt / 1000) * 4)
      pointer.x += (pointer.tx - pointer.x) * k
      pointer.y += (pointer.ty - pointer.y) * k
      globe.render((now - t0) / 1000 + optsRef.current.storyStart, pointer, optsRef.current.getView?.())
    }

    // Grab-and-spin: the scene turns with the pointer while it is held and
    // coasts after release. Paused or reduced motion: each move draws one frame.
    let held: { id: number; x: number; y: number } | null = null
    const onDown = (e: PointerEvent) => {
      if (!globe || !optsRef.current.interactive || (e.pointerType === 'mouse' && e.button !== 0)) return
      held = { id: e.pointerId, x: e.clientX, y: e.clientY }
      host.setPointerCapture?.(e.pointerId)
      globe.dragStart(performance.now())
    }
    const onMove = (e: PointerEvent) => {
      if (!globe || !held || e.pointerId !== held.id) return
      globe.dragMove(e.clientX - held.x, e.clientY - held.y, performance.now())
      held.x = e.clientX
      held.y = e.clientY
      if (reduce || globePause.get()) globe.render(optsRef.current.storyStart, pointer, optsRef.current.getView?.())
    }
    const onUp = (e: PointerEvent) => {
      if (!globe || !held || e.pointerId !== held.id) return
      held = null
      globe.dragEnd(performance.now())
    }
    host.addEventListener('pointerdown', onDown)
    host.addEventListener('pointermove', onMove)
    host.addEventListener('pointerup', onUp)
    host.addEventListener('pointercancel', onUp)

    let started = false
    const start = async () => {
      if (started) return
      started = true
      // Visitors who ask for less data (Save-Data) or are on a 2G link keep the
      // static CSS glow and never download the globe chunk.
      const conn = (navigator as Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }).connection
      if (conn?.saveData || conn?.effectiveType === 'slow-2g' || conn?.effectiveType === '2g') return
      const mod = await import('./probe-globe-scene')
      if (disposed) return
      const c = document.createElement('canvas')
      c.className = 'absolute inset-0 size-full'
      host.prepend(c)
      try {
        globe = mod.createGlobe({ host, canvas: c })
      } catch {
        // No WebGL: the CSS glow stays as the whole effect.
        c.remove()
        return
      }
      canvas = c
      layout()
      if (reduce) {
        still()
        return
      }
      setLive(true)
      raf = requestAnimationFrame(frame)
    }

    const trigger = optsRef.current.trigger ?? 'idle'
    let idle: number | undefined
    let near: IntersectionObserver | undefined
    if (trigger === 'near') {
      near = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) {
            near?.disconnect()
            void start()
          }
        },
        { rootMargin: '600px' },
      )
      near.observe(host)
    } else {
      idle = window.requestIdleCallback
        ? window.requestIdleCallback(() => void start(), { timeout: 2500 })
        : window.setTimeout(() => void start(), 300)
    }

    const ro = new ResizeObserver(() => {
      layout()
      if (reduce) still()
    })
    ro.observe(host)

    const io = new IntersectionObserver(
      (entries) => {
        visible = entries[0]?.isIntersecting ?? true
      },
      { rootMargin: '80px' },
    )
    io.observe(host)
    if (!reduce) window.addEventListener('pointermove', onPointer, { passive: true })
    const unsubscribePause = globePause.subscribe(() => {
      prev = 0
    })

    return () => {
      disposed = true
      if (idle !== undefined) {
        if (window.cancelIdleCallback && typeof idle === 'number') window.cancelIdleCallback(idle)
        else window.clearTimeout(idle)
      }
      near?.disconnect()
      if (raf) cancelAnimationFrame(raf)
      ro.disconnect()
      io.disconnect()
      unsubscribePause()
      window.removeEventListener('pointermove', onPointer)
      host.removeEventListener('pointerdown', onDown)
      host.removeEventListener('pointermove', onMove)
      host.removeEventListener('pointerup', onUp)
      host.removeEventListener('pointercancel', onUp)
      globe?.dispose()
      canvas?.remove()
      stillRef.current = () => {}
    }
  }, [])

  return { live, renderStill: () => stillRef.current() }
}
